// These are used as feature flags
export enum OrgPermission {
  GeminiModels = 'geminiModels',
  RelativityUsage = 'relativityUsage',
  AdminApproval = 'adminApproval',
  Require2FANonSSO = 'require2faNonSSO',
  Feedback = 'feedback',
  UsePropelAdminRoleAsDefault = 'usePropelAdminRoleAsDefault',
  ProceduralTimelines = 'proceduralTimelines',
  GenericFileMetadata = 'genericFileMetadata',
  AlwaysUseForms = 'alwaysUseForms',
  PstUploads = 'pstUploads',
  ExternalUserTermsOfUse = 'externalUserTermsOfUse',
  DisableBillingForKimThreads = 'disableBillingForKimThreads',
  UserBlocking = 'userBlocking',
}

export interface OrgPermissionConfig {
  id: string;
  permission: OrgPermission;
  title: string;
  description: string;
}

export const ORG_PERMISSION_CONFIGS: readonly OrgPermissionConfig[] = [
  {
    id: 'gemini-toggle',
    permission: OrgPermission.GeminiModels,
    title: 'Gemini Models',
    description: 'Whether or not Gemini models are enabled for this organisation.',
  },
  {
    id: 'admin-approval-toggle',
    permission: OrgPermission.AdminApproval,
    title: 'Admin Approval',
    description: 'Whether the admin approval feature is enabled for this organisation.',
  },
  {
    id: 'require-2fa-toggle',
    permission: OrgPermission.Require2FANonSSO,
    title: 'Require 2FA non-SSO users',
    description:
      'Require non-SSO users to enable two-factor authentication before accessing the platform.',
  },
  {
    id: 'feedback-toggle',
    permission: OrgPermission.Feedback,
    title: 'In-app feedback',
    description: 'Whether the in-app feedback pop-ups are enabled for this organisation.',
  },
  {
    id: 'relativity-usage-toggle',
    permission: OrgPermission.RelativityUsage,
    title: 'Relativity Integration',
    description: 'Whether the Relativity integration is enabled for this organisation.',
  },
  {
    id: 'use-propel-admin-role-as-default-toggle',
    permission: OrgPermission.UsePropelAdminRoleAsDefault,
    title: 'Use Propel admin role as default',
    description:
      'When enabled, users added via PropelAuth with the Admin role are assigned the Administrator role in Wexler. When disabled, all new users get the default role.',
  },
  {
    id: 'procedural-timelines-toggle',
    permission: OrgPermission.ProceduralTimelines,
    title: 'Procedural Timelines',
    description:
      'Whether the procedural timelines feature is enabled for this organisation. Allows uploading court orders and directions to extract procedural events.',
  },
  {
    id: 'generic-file-metadata-toggle',
    permission: OrgPermission.GenericFileMetadata,
    title: 'Generic File Metadata',
    description:
      'Whether the generic file metadata system is enabled for this organisation. Allows attaching document metadata from load files.',
  },
  {
    id: 'always-use-forms-toggle',
    permission: OrgPermission.AlwaysUseForms,
    title: 'Always use forms',
    description: 'Whether forms are always used in pipeline extraction for this organisation.',
  },
  {
    id: 'pst-uploads-toggle',
    permission: OrgPermission.PstUploads,
    title: 'PST uploads',
    description:
      'Allow uploading Outlook .pst mail archives, which are unpacked server-side into individual emails and attachments.',
  },
  {
    id: 'external-user-terms-of-use-toggle',
    permission: OrgPermission.ExternalUserTermsOfUse,
    title: 'Require terms of use acceptance for non-SSO users',
    description:
      "Require non-SSO (external) users to accept the organisation's terms of use before using the platform. Terms files (terms.md and terms.pdf) must be uploaded to the org S3 bucket under {orgId}/terms-of-use/.",
  },
  {
    id: 'disable-kim-thread-billing-toggle',
    permission: OrgPermission.DisableBillingForKimThreads,
    title: 'Disable billing for Kim threads',
    description:
      'When enabled, Kim threads do not contribute towards billed credits for this organisation — excluded from usage reporting, report downloads, and blocking/limit enforcement. Activity counts remain visible.',
  },
  {
    id: 'user-blocking-toggle',
    permission: OrgPermission.UserBlocking,
    title: 'User blocking',
    description:
      'Allow organisation administrators to block and unblock users in their organisation.',
  },
];
